'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

type PermissionScope =
  | 'bookings' | 'visitors' | 'loyalty' | 'reports' | 'settings' |'campaigns' | 'teesheet' | 'memberships' | 'staff_management';

type StaffRole = 'admin' | 'manager' | 'staff';

interface StaffAccount {
  id: string;
  staff_role: StaffRole;
  permissions: PermissionScope[];
  is_active: boolean;
  full_name: string;
  email: string;
}

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [staffAccount, setStaffAccount] = useState<StaffAccount | null>(null);
  const supabase = createClient();

  const fetchUserRole = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', userId)
        .single();
      setUserRole(data?.role ?? 'visitor');
    } catch {
      setUserRole('visitor');
    }
  };

  const fetchStaffAccount = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('staff_accounts')
        .select('id, staff_role, permissions, is_active, full_name, email')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();
      setStaffAccount(data ?? null);
    } catch {
      setStaffAccount(null);
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
        fetchStaffAccount(session.user.id);
      } else {
        setUserRole(null);
        setStaffAccount(null);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
        fetchStaffAccount(session.user.id);
      } else {
        setUserRole(null);
        setStaffAccount(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up — creates Supabase auth account + triggers user_profiles row via DB trigger
  const signUp = async (email: string, password: string, metadata: { fullName?: string; avatarUrl?: string } = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: 'visitor',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserRole(null);
    setStaffAccount(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Role helpers
  const isAdmin = userRole === 'admin';
  const isGolfManager = userRole === 'golf_manager';
  const isStaff = isAdmin || isGolfManager || (staffAccount !== null && staffAccount.is_active);

  // Staff permission helper
  const hasPermission = (perm: PermissionScope): boolean => {
    if (isAdmin) return true;
    if (!staffAccount) return false;
    return staffAccount.permissions.includes(perm);
  };

  const value = {
    user,
    session,
    loading,
    userRole,
    isAdmin,
    isGolfManager,
    isStaff,
    staffAccount,
    hasPermission,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
